import { FormEvent, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { ProfileNotificationsSection } from '../components/ProfileNotificationsSection';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

const DISPLAY_NAME_ERRORS: Record<string, string> = {
  display_name_required: 'profile.nameRequired',
  display_name_invalid: 'profile.nameInvalid',
  display_name_taken: 'profile.nameTaken',
};

function PencilIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 0 0 0-1.42l-2.34-2.34a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z"
      />
    </svg>
  );
}

function ProfileAvatar({ name, avatarUrl }: { name: string; avatarUrl?: string }) {
  const initial = (name.trim()[0] || '?').toUpperCase();
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className="profile-avatar-img" />;
  }
  return <span className="profile-avatar-initial" aria-hidden="true">{initial}</span>;
}

function ProfileNameEdit() {
  const { user, updateDisplayName } = useAuth();
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  const openEdit = () => {
    setValue(user.displayName);
    setError('');
    setEditing(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const cancel = () => {
    setEditing(false);
    setError('');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await updateDisplayName(value);
      setEditing(false);
    } catch (err: unknown) {
      const code = (err as { data?: { error?: string } }).data?.error;
      const key = code && DISPLAY_NAME_ERRORS[code];
      setError(key ? t(key) : t('profile.nameError'));
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <form className="profile-name-form" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={32}
          disabled={busy}
          aria-label={t('profile.nameLabel')}
        />
        <button type="submit" className="profile-name-save" disabled={busy || !value.trim()}>
          {busy ? '…' : '✓'}
        </button>
        <button type="button" className="profile-name-cancel" onClick={cancel} disabled={busy}>
          ✕
        </button>
        {error && <p className="profile-name-error" role="alert">{error}</p>}
      </form>
    );
  }

  return (
    <div className="profile-name-row">
      <h1 className="profile-name">{user.displayName}</h1>
      <button
        type="button"
        className="profile-name-edit"
        onClick={openEdit}
        aria-label={t('profile.editName')}
      >
        <PencilIcon />
      </button>
    </div>
  );
}

function ProfileDeleteSection() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!user || user.isAdmin) return null;

  const confirmWord = t('profile.deleteConfirmWord');
  const canDelete = confirmText.trim().toUpperCase() === confirmWord.toUpperCase();

  const handleDelete = async () => {
    if (!canDelete || busy) return;
    setBusy(true);
    setError('');
    try {
      await api('/auth/me', { method: 'DELETE' });
      logout();
      window.location.replace('/');
    } catch {
      setError(t('common.error'));
      setBusy(false);
    }
  };

  return (
    <section className="profile-delete">
      {!open ? (
        <button
          type="button"
          className="link-muted profile-delete-open"
          onClick={() => setOpen(true)}
        >
          {t('profile.deleteAccount')}
        </button>
      ) : (
        <div className="profile-delete-confirm" role="alertdialog" aria-label={t('profile.deleteAccount')}>
          <h2>{t('profile.deleteAccount')}</h2>
          <p className="muted">{t('profile.deleteWarning')}</p>
          <label htmlFor="profile-delete-input" className="muted">
            {t('profile.deleteTypeToConfirm').replace('{word}', confirmWord)}
          </label>
          <input
            id="profile-delete-input"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoComplete="off"
          />
          {error && <p className="contact-error" role="alert">{error}</p>}
          <div className="profile-delete-actions">
            <button
              type="button"
              className="btn secondary"
              onClick={() => { setOpen(false); setConfirmText(''); setError(''); }}
              disabled={busy}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn ghost profile-delete-danger"
              onClick={handleDelete}
              disabled={!canDelete || busy}
            >
              {busy ? t('common.loading') : t('profile.deleteConfirmCta')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function ProfilePage() {
  const { user, guest, logout } = useAuth();

  if (!user) {
    return (
      <main className="page profile">
        <div className="profile-card profile-card--empty">
          <p className="login-brand">Dezoom</p>
          <h1>{t('profile.title')}</h1>
          <p className="profile-empty-text">{t('profile.loginPrompt')}</p>
          <Link to="/login" className="btn primary block">{t('landing.login')}</Link>
          <Link to="/" className="link-muted profile-back">{t('auth.back')}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page profile">
      <div className="profile-card">
        <div className="profile-hero">
          <ProfileAvatar name={user.displayName} avatarUrl={user.avatarUrl} />
          <ProfileNameEdit />
          <p className="profile-email">{user.email}</p>
          <span className={`profile-plan-badge${user.isPremium ? ' is-premium' : ''}`}>
            {user.isPremium ? t('premium.active') : t('profile.planFree')}
          </span>
        </div>

        <div className="profile-stats">
          <div className="profile-stat">
            <span className="profile-stat-value">{user.streak}</span>
            <span className="profile-stat-label">{t('profile.streak')}</span>
          </div>
        </div>

        <ProfileNotificationsSection />

        <div className="profile-actions">
          <Link to="/play" className="btn primary block">{t('profile.playToday')}</Link>
          {!user.isPremium && (
            <Link to="/premium" className="btn secondary block">{t('premium.subscribe')}</Link>
          )}
          <Link to="/archives" className="btn secondary block">{t('archives.title')}</Link>
          {user && !guest && (
            <Link to="/friends" className="btn secondary block">{t('friends.title')}</Link>
          )}
        </div>

        <nav className="profile-nav" aria-label={t('nav.main')}>
          <Link to="/leaderboard">{t('nav.leaderboards')}</Link>
          {user && !guest && (
            <Link to="/leaderboard/amis">{t('leaderboard.scopeFriends')}</Link>
          )}
          <Link to="/archives">{t('archives.title')}</Link>
          <Link to="/top">{t('nav.top')}</Link>
          {user.isAdmin && <Link to="/admin">{t('nav.admin')}</Link>}
          <Link to="/">{t('gallery.home')}</Link>
        </nav>

        <button type="button" className="btn ghost block profile-logout" onClick={logout}>
          {t('profile.logout')}
        </button>

        <ProfileDeleteSection />
      </div>
    </main>
  );
}
